import { useCallback } from 'react'
import { useInfiniteQuery, useQueryClient } from 'react-query'
import { PrescricaoType, useDebounce } from '_metronic/helpers'
import {
  exameDoCatalogo,
  filtroDeBusca,
  formulaDoCatalogo,
  medicamentoDoCatalogo,
  orientacaoDoCatalogo,
} from '../core/montagem/catalogo'
import { CategoriaItem, ItemCatalogo } from '../core/montagem/itens'
import {
  getExames,
  getFormulas,
  getMedicamentos,
  getOrientacoes,
  inativarExame,
  inativarMedicamento,
  inativarOrientacao,
} from '../core/_requests'

const TAMANHO_PAGINA = 20
const CHAVE = 'prescricao-catalogo'

type Pagina = { itens: ItemCatalogo[]; pagina: number; ultima: boolean }

const buscarPagina = async (
  categoria: CategoriaItem,
  termo: string,
  pagina: number
): Promise<Pagina> => {
  const filtro = filtroDeBusca(categoria, termo)
  const query = `size=${TAMANHO_PAGINA}&page=${pagina}${
    filtro ? `&search=${encodeURIComponent(filtro)}` : ''
  }&sort=nome`

  const resposta = await (async () => {
    switch (categoria) {
      case PrescricaoType.Medicamento: {
        const r = await getMedicamentos(query)
        return { ...r, itens: (r.data ?? []).map(medicamentoDoCatalogo) }
      }
      case PrescricaoType.Formula: {
        const r = await getFormulas(query)
        return { ...r, itens: (r.data ?? []).map(formulaDoCatalogo) }
      }
      case PrescricaoType.Exame: {
        const r = await getExames(query)
        return { ...r, itens: (r.data ?? []).map(exameDoCatalogo) }
      }
      case PrescricaoType.Orientacoes: {
        const r = await getOrientacoes(query)
        return { ...r, itens: (r.data ?? []).map(orientacaoDoCatalogo) }
      }
    }
  })()

  return {
    // Só a busca de medicamento filtra inativos no backend; os demais chegam com `ativo` false.
    itens: resposta.itens.filter((item) => item.ativo !== false),
    pagina,
    ultima: pagina + 1 >= resposta.totalPages,
  }
}

/** Busca paginada do catálogo de uma categoria, com debounce e carregamento por rolagem. */
export const useCatalogoBusca = (categoria: CategoriaItem, termo: string, habilitada: boolean) => {
  const termoEstavel = useDebounce(termo.trim(), 400)
  const queryClient = useQueryClient()

  const consulta = useInfiniteQuery(
    [CHAVE, categoria, termoEstavel],
    ({ pageParam = 0 }) => buscarPagina(categoria, termoEstavel, pageParam),
    {
      enabled: habilitada,
      getNextPageParam: (ultima) => (ultima.ultima ? undefined : ultima.pagina + 1),
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    }
  )

  const resultados = consulta.data?.pages.flatMap((pagina) => pagina.itens) ?? []

  /** Inativa um item do próprio médico e tira ele da busca. */
  const inativar = useCallback(
    async (item: ItemCatalogo) => {
      if (!item.id) return
      switch (item.categoria) {
        case PrescricaoType.Medicamento:
          await inativarMedicamento(item.id, {
            nome: item.nome,
            codigo: item.codigo,
            concentracao: item.concentracao,
            apresentacao: item.apresentacao,
            // O backend exige o receituário (a tarja é derivada dele).
            receituario: item.receituario,
          })
          break
        case PrescricaoType.Exame:
          await inativarExame(item.id, {
            nome: item.nome,
            codigo: item.codigo,
            tipo: item.tipoExame,
            apac: item.apac,
          })
          break
        case PrescricaoType.Orientacoes:
          await inativarOrientacao(item.id)
          break
        default:
          return
      }
      await queryClient.invalidateQueries([CHAVE, item.categoria])
    },
    [queryClient]
  )

  return {
    resultados,
    carregando: consulta.isLoading || (consulta.isFetching && !consulta.isFetchingNextPage),
    carregandoMais: consulta.isFetchingNextPage,
    temMais: !!consulta.hasNextPage,
    carregarMais: consulta.fetchNextPage,
    erro: consulta.error,
    termoBuscado: termoEstavel,
    inativar,
  }
}
